import { cn } from "@-/com.npmjs.cn";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { Pressable, View } from "@-/com.npmjs.react-native";
import Animated, { FadeOut, ReduceMotion } from "@-/com.npmjs.react-native-reanimated";
import { HanStreakFlame } from "@-/dev.improve.haniverse.assets.icons";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import {
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
} from "@-/dev.improve.haniverse.components.ui.popover";
import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@-/dev.improve.haniverse.components.ui.tooltip";
import {
  REVIEW_RECHARGE_FULL,
  STREAK_DAILY_RECHARGE_POINTS,
  useDueCharactersCountQueryOptions,
  useDueRowsCountQueryOptions,
  useHasReviewedAtLeastOnceQueryOptions,
  useReviewRechargeQueryOptions,
  useStreakPointsTodayQueryOptions,
} from "@-/dev.improve.haniverse.lib.cards.review";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import {
  useDailyNewGraphemeAllowanceQueryOptions,
  useTimeUntilNextDailyNewGraphemes,
} from "@-/dev.improve.haniverse.lib.learn.new-grapheme-limit";
import { useDailyNewCardsSettingsPageNavigator } from "@-/dev.improve.haniverse.routes.nav.app.settings.daily-new-cards.navigator";
import { useEffect, useState, type ReactNode } from "react";
import type { LearnReviewRechargeGate } from "#learn-review-recharge-gate";
import {
  type LearnReviewRechargeInk,
  LearnReviewRechargeMeter,
} from "#learn-review-recharge-meter";

/** Below this the band is the compact bar: no room for the icon or the toggle label. */
const CONTENT_MIN_HEIGHT = 14;
/** LuBatteryCharging's ink (y 6–18 of its 24 viewBox, stroke included) as a share of its size. */
const ICON_INK_HEIGHT_RATIO = 12 / 24;
/** Han Composite SemiBold's cap height (OS/2 sCapHeight 714 of 1000 units per em). */
const LABEL_CAP_HEIGHT_EM = 0.714;
/** The label's switch track against the icon size: taller than the text, yet clear of the bar's rim. */
const SWITCH_HEIGHT_RATIO = 0.85;
/** How long "New Cards: OFF" stays after switching off a spent bar, before it fades. */
const OFF_LABEL_LINGER_MS = 1200;

/**
 * Review recharge, above the portrait card or below the landscape card's left control stack.
 * Landscape fills upwards, with the battery at the bottom and the streak flame at the top.
 * Charge builds up by reviewing. Each committed
 * review adds to it (Again +1, Hard/Good/Easy +5, up to 100); revealing a new character while
 * reviews are due drains it to 0 at once. Undone reviews drop out, so Undo walks it back too. The
 * battery icon explains the bar in a tooltip.
 *
 * Until the learner has reviewed at least once, the bar stays faded and inactive: tapping it
 * explains that it activates after the first review. Learning new cards alone does not wake it.
 *
 * Once it is full the bar becomes the New Cards toggle, OFF until pressed. ON queues new
 * curriculum cards: the card on screen stays, and the next swipe opens a new card instead of a
 * review. Revealing that card's first new character drains the bar, and the rest of its lesson group
 * follows automatically; when the lesson group ends, reviews resume and the toggle returns to OFF. The
 * label shows only while the bar is full or New Cards is ON. Switching OFF a spent bar shows "New
 * Cards: OFF" briefly, then fades the label; it cannot come back ON until the bar is full again.
 *
 * Once today's new-character allowance is spent (Daily New Cards, or the free tier's cap) there
 * is no new card to spend a charge on: the reviews still charge the bar, but New Cards stays OFF
 * even when it is full ("New Cards: OFF"). Tapping the bar then opens a popover saying when the
 * next new card unlocks, with a button to Settings → Daily New Cards.
 *
 * The fill is yellow while charging and orange while the charge is being spent: a new card is on
 * screen while reviews are due. Switching New Cards ON only queues new cards, so the bar stays
 * yellow until the swipe that opens one. The bar is flat, drawn like the card below it. The
 * battery, label and switch are bare strokes: black over the fill and theme ink over the empty
 * track, split exactly where the fill's edge crosses them.
 *
 * The fill fades — the bar looks disabled, never gone — when the review queue is empty or the
 * learner has never reviewed: the balance is kept, but nothing adds to or spends it.
 *
 * Next to the battery, the bar says how many characters are left in the review queue ("12
 * characters left"), each counted once however many of its cards are due. Landscape has no room
 * for it beside the New Cards label, so there it shows only while that label is hidden.
 *
 * The streak flame sits at the right end: gray until today's reviews earn
 * {@link STREAK_DAILY_RECHARGE_POINTS} streak points, orange once today keeps the streak. Tapping it
 * explains how many points are left.
 */
export function LearnReviewRechargeBar({
  currentCardKind,
  gate: _gate,
  height,
  top,
}: {
  currentCardKind: "lesson" | "review" | "slide";
  gate: LearnReviewRechargeGate;
  height: number;
  top: number;
}) {
  const { diameter, isLandscape } = useToolbarGridMetrics();
  const thickness = isLandscape ? diameter : height;
  const rechargeQuery = useQuery({ ...useReviewRechargeQueryOptions(), throwOnError: false });
  const dailyNewGraphemeAllowanceQuery = useQuery({
    ...useDailyNewGraphemeAllowanceQueryOptions(),
    throwOnError: false,
  });
  // Nothing to spend a charge on until tomorrow: New Cards stays OFF however full the bar is.
  const isDailyLimitReached = dailyNewGraphemeAllowanceQuery.data?.isLimitReached === true;
  const dueQuery = useQuery({ ...useDueRowsCountQueryOptions(), throwOnError: false });
  const hasReviewedQuery = useQuery({
    ...useHasReviewedAtLeastOnceQueryOptions(),
    throwOnError: false,
  });
  const streakPointsQuery = useQuery({
    ...useStreakPointsTodayQueryOptions(),
    throwOnError: false,
  });
  const recharge = rechargeQuery.data ?? 0;
  const streakPointsToday = streakPointsQuery.data;
  const dueReviewCount = dueQuery.data;
  // Re-keyed on every review; keep the last count on screen meanwhile so it never blinks.
  const dueCharactersQuery = useQuery({
    ...useDueCharactersCountQueryOptions(),
    placeholderData: keepPreviousData,
    throwOnError: false,
  });
  const dueCharacterCount = dueCharactersQuery.data ?? 0;
  const dueCharactersLabel = `${dueCharacterCount} ${dueCharacterCount === 1 ? "character" : "characters"} left`;
  const isLoading = rechargeQuery.isPending || dueQuery.isPending || hasReviewedQuery.isPending;
  const isUnavailable = rechargeQuery.isError || dueQuery.isError || hasReviewedQuery.isError;
  const [storedNewCardsEnabled, setNewCardsEnabled] = useDeviceSetting(
    "reviewRechargeNewCardsEnabled",
  );
  // A setting left ON reads OFF at the daily limit: the deck opens no new card either way.
  const newCardsEnabled = storedNewCardsEnabled && !isDailyLimitReached;
  const isFull = recharge >= REVIEW_RECHARGE_FULL;
  // Never-reviewed learners (new cards only so far) keep the bar inactive until a first review.
  const isInactive = !isLoading && !isUnavailable && hasReviewedQuery.data === false;
  const isFaded = isInactive || dueReviewCount === 0;
  const isSpending = !isFaded && currentCardKind !== "review";
  const isHeldAtDailyLimit = !isLoading && !isUnavailable && !isInactive && isDailyLimitReached;
  // ON stays switchable (including on a new card) so a learner can stop spending at any time.
  // Never-reviewed stays non-toggleable even if New Cards was left ON from an earlier session.
  const isToggleable =
    !isLoading &&
    !isUnavailable &&
    !isInactive &&
    !isDailyLimitReached &&
    (newCardsEnabled || (isFull && !isFaded && !isSpending));
  const showsContent = thickness >= CONTENT_MIN_HEIGHT;
  const contentSize = Math.round(thickness * 0.6);
  // The label's capitals stand exactly as tall as the icon's drawn battery.
  const labelFontSize = isLandscape
    ? 11
    : (contentSize * ICON_INK_HEIGHT_RATIO) / LABEL_CAP_HEIGHT_EM;
  const labelLineHeight = isLandscape ? 14 : height;
  const switchHeight = Math.round(contentSize * SWITCH_HEIGHT_RATIO);
  const switchWidth = Math.round(switchHeight * 1.75);
  const switchInset = Math.max(2, Math.round(switchHeight * 0.15));
  const switchGap = Math.round(switchHeight * 0.5);
  const [isOffLabelLingering, setIsOffLabelLingering] = useState(false);
  useEffect(() => {
    if (!isOffLabelLingering) {
      return;
    }
    const timer = setTimeout(setIsOffLabelLingering, OFF_LABEL_LINGER_MS, false);
    return () => clearTimeout(timer);
  }, [isOffLabelLingering]);
  // A full bar held at the daily limit keeps saying "New Cards: OFF".
  const showsLabel =
    showsContent && (isToggleable || isOffLabelLingering || (isHeldAtDailyLimit && isFull));
  const showsDueCharacters =
    showsContent && !isFaded && dueCharacterCount > 0 && !(isLandscape && showsLabel);
  const toggleNewCards = () => {
    const next = !newCardsEnabled;
    setNewCardsEnabled(next);
    // Off on a spent bar: keep "OFF" on screen for a moment so the change reads, then fade.
    setIsOffLabelLingering(!next && !isFull);
  };
  // The icon's own tap target: the bar's height, square, at its left end.
  const iconSlotWidth = isLandscape ? diameter : height + 8;
  const iconSlotClassName = isLandscape
    ? "absolute inset-x-0 bottom-0 justify-center"
    : "absolute inset-y-0 left-0 justify-center";
  const iconSlotStyle = isLandscape ? { height: iconSlotWidth } : { width: iconSlotWidth };
  // Just past the battery: beside it in portrait, above it in landscape.
  const dueCharactersSlotClassName = isLandscape
    ? "absolute inset-x-0 items-center"
    : "absolute inset-y-0 justify-center";
  const dueCharactersSlotStyle = isLandscape ? { bottom: iconSlotWidth } : { left: iconSlotWidth };
  // Drawn by the meter twice (over the track and clipped to the fill), so each copy takes its ink.
  const renderInk = (ink: LearnReviewRechargeInk) => {
    const isOnFill = ink === "fill";
    const switchTrackClassName = isOnFill ? "bg-black" : "bg-foreground";
    return (
      <>
        {showsContent ? (
          <View className={cn(iconSlotClassName, "items-center")} style={iconSlotStyle}>
            <Icon
              className={isOnFill ? "text-black" : "text-foreground"}
              name="LuBatteryCharging"
              size={contentSize}
            />
          </View>
        ) : null}
        {showsDueCharacters ? (
          <View className={dueCharactersSlotClassName} style={dueCharactersSlotStyle}>
            <Text
              className={cn(
                "text-center font-sans font-semibold",
                isOnFill ? "text-black" : "text-foreground",
              )}
              numberOfLines={isLandscape ? 2 : 1}
              style={{ fontSize: labelFontSize, lineHeight: labelLineHeight }}
              testID="learn-review-recharge-due-characters"
            >
              {isLandscape ? `${dueCharacterCount}\nleft` : dueCharactersLabel}
            </Text>
          </View>
        ) : null}
        {showsLabel ? (
          <Animated.View
            className={cn(
              "absolute inset-0 items-center justify-center",
              !isLandscape && "flex-row",
            )}
            exiting={FadeOut.duration(400).reduceMotion(ReduceMotion.System)}
            style={{ gap: switchGap }}
          >
            <Text
              className={cn(
                "text-center font-sans font-semibold",
                isOnFill ? "text-black" : "text-foreground",
              )}
              numberOfLines={isLandscape ? 3 : 1}
              style={{ fontSize: labelFontSize, lineHeight: labelLineHeight }}
            >
              {isLandscape
                ? `New\nCards\n${newCardsEnabled ? "ON" : "OFF"}`
                : newCardsEnabled
                  ? "New Cards: ON"
                  : "New Cards: OFF"}
            </Text>
            {/* A switch after the label, so the full bar reads as something to tap. */}
            <View
              className={cn(
                "rounded-full border-[1.5px]",
                isOnFill ? "border-black" : "border-foreground",
                newCardsEnabled ? "items-end" : "items-start",
                newCardsEnabled && switchTrackClassName,
              )}
              style={{ height: switchHeight, padding: switchInset, width: switchWidth }}
            >
              <View
                className={cn(
                  "aspect-square flex-1 rounded-full",
                  newCardsEnabled
                    ? isOnFill
                      ? "bg-white"
                      : "bg-background"
                    : switchTrackClassName,
                )}
              />
            </View>
          </Animated.View>
        ) : null}
      </>
    );
  };
  const bar = (
    <>
      <LearnReviewRechargeMeter
        fraction={recharge / REVIEW_RECHARGE_FULL}
        isFaded={isFaded}
        isSpending={isSpending}
        vertical={isLandscape}
      >
        {renderInk}
      </LearnReviewRechargeMeter>
      {isLoading ? (
        <Skeleton
          className="absolute inset-0 rounded-full"
          pointerEvents="none"
          testID="learn-review-recharge-loading"
        />
      ) : null}
    </>
  );
  const inactiveBar = (
    <Tooltip delayDuration={0}>
      <TooltipTrigger
        accessibilityHint="Activates after you review your first card"
        accessibilityLabel="Review recharge inactive"
        accessibilityRole="button"
        className="size-full"
        hitSlop={{ bottom: 8, top: 8 }}
        testID="learn-review-recharge-inactive"
      >
        {bar}
      </TooltipTrigger>
      <TooltipContent className="max-w-72" side="bottom">
        <Text className="font-semibold">Review Recharge</Text>
        <Text>
          This bar activates once you review your first card. Until then it stays off while you
          learn new cards.
        </Text>
      </TooltipContent>
    </Tooltip>
  );
  return (
    <View
      className={isLandscape ? "absolute bottom-2.25 left-2.25" : "absolute inset-x-2.25"}
      pointerEvents="box-none"
      // Four circles (More, Undo, Redo, Environment) and five 9pt gaps precede the vial.
      style={isLandscape ? { top: diameter * 4 + 45, width: diameter } : { height, top }}
      testID="learn-review-recharge"
    >
      {isToggleable ? (
        <Pressable
          accessibilityHint="When on, your next swipe opens a new card instead of a review"
          accessibilityLabel="New cards"
          accessibilityRole="switch"
          accessibilityState={{ checked: newCardsEnabled }}
          className="size-full active:opacity-80"
          hitSlop={{ bottom: 8, top: 8 }}
          onPress={toggleNewCards}
          testID="learn-review-recharge-new-cards-toggle"
        >
          {bar}
        </Pressable>
      ) : isHeldAtDailyLimit ? (
        <LearnReviewRechargeDailyLimitPopover
          accessibilityLabel={`New cards off until tomorrow. Review recharge ${recharge} of ${REVIEW_RECHARGE_FULL}${showsDueCharacters ? `, ${dueCharactersLabel}` : ""}`}
        >
          {bar}
        </LearnReviewRechargeDailyLimitPopover>
      ) : isInactive ? (
        inactiveBar
      ) : (
        <View
          accessibilityLabel={
            isLoading
              ? "Loading review recharge"
              : isUnavailable
                ? "Review recharge unavailable"
                : `Review recharge ${recharge} of ${REVIEW_RECHARGE_FULL}${showsDueCharacters ? `, ${dueCharactersLabel}` : ""}`
          }
          accessibilityRole="progressbar"
          accessibilityState={{ busy: isLoading }}
          accessibilityValue={
            isLoading || isUnavailable
              ? undefined
              : { max: REVIEW_RECHARGE_FULL, min: 0, now: recharge }
          }
          className="size-full"
          pointerEvents="none"
        >
          {bar}
        </View>
      )}
      {/*
       * A sibling above the bar, so tapping the icon explains the bar without toggling it. The
       * meter draws the battery itself; this trigger only covers its slot.
       * Inactive: no trigger, so taps fall through to the bar's activation tooltip.
       */}
      {showsContent && !isInactive ? (
        <View className={iconSlotClassName} pointerEvents="box-none" style={iconSlotStyle}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger
              accessibilityLabel="What is review recharge?"
              accessibilityRole="button"
              className="h-full"
              hitSlop={{ bottom: 8, top: 8 }}
              testID="learn-review-recharge-info"
            />
            <TooltipContent className="max-w-72" side="bottom">
              <Text className="font-semibold">Review Recharge</Text>
              <Text>
                Reviews recharge this bar: Again adds 1, Hard, Good and Easy add 5. When it&rsquo;s
                full, tap the bar to turn New Cards on, and your next swipe brings a new card. While
                you still have reviews due, that new card uses the whole charge and brings in the
                rest of its lesson group.
              </Text>
            </TooltipContent>
          </Tooltip>
        </View>
      ) : null}
      {showsContent && streakPointsToday !== undefined ? (
        <View
          className={
            isLandscape
              ? "absolute inset-x-0 top-0 justify-center"
              : "absolute inset-y-0 right-0 justify-center"
          }
          // Inactive: let taps fall through to the bar's activation tooltip.
          pointerEvents={isInactive ? "none" : "box-none"}
          style={isLandscape ? { height: iconSlotWidth } : { width: iconSlotWidth }}
        >
          <LearnReviewRechargeStreakFlame
            isInactive={isInactive}
            pointsToday={streakPointsToday}
            size={contentSize}
          />
        </View>
      ) : null}
    </View>
  );
}

/**
 * The bar once today's new-character allowance is spent: a tap says when the next new card
 * unlocks, and offers Settings → Daily New Cards, where the limit is changed.
 */
function LearnReviewRechargeDailyLimitPopover({
  accessibilityLabel,
  children,
}: {
  accessibilityLabel: string;
  children: ReactNode;
}) {
  const dailyNewCardsPageNavigator = useDailyNewCardsSettingsPageNavigator();
  return (
    <Popover>
      <PopoverTrigger
        accessibilityHint="Shows when the next new card unlocks"
        accessibilityLabel={accessibilityLabel}
        accessibilityRole="button"
        className="size-full active:opacity-80"
        hitSlop={{ bottom: 8, top: 8 }}
        testID="learn-review-recharge-daily-limit"
      >
        {children}
      </PopoverTrigger>
      <PopoverContent className="w-64 gap-3" side="bottom">
        <LearnReviewRechargeNextNewCard />
        <PopoverClose asChild onPress={() => dailyNewCardsPageNavigator.push.mutate()}>
          <Button testID="learn-review-recharge-daily-limit-settings-button" variant="outline">
            <Text>Daily New Cards</Text>
          </Button>
        </PopoverClose>
      </PopoverContent>
    </Popover>
  );
}

/** Mounted only while the popover is open, so the countdown ticks only while it is read. */
function LearnReviewRechargeNextNewCard() {
  const timeUntilNextNewCard = useTimeUntilNextDailyNewGraphemes();
  return (
    <View className="gap-1">
      <Text className="font-semibold">{`Next new card in ${timeUntilNextNewCard}`}</Text>
      <Text colorVariant="muted">
        You&rsquo;ve learned today&rsquo;s new cards, so New Cards stays off until then. Your
        reviews still charge the bar.
      </Text>
    </View>
  );
}

/** Today's streak at the bar's right end: gray while the streak is at stake, orange once kept. */
function LearnReviewRechargeStreakFlame({
  isInactive,
  pointsToday,
  size,
}: {
  isInactive: boolean;
  pointsToday: number;
  size: number;
}) {
  const isTodaySecured = pointsToday >= STREAK_DAILY_RECHARGE_POINTS;
  const pointsLeft = STREAK_DAILY_RECHARGE_POINTS - pointsToday;
  const flame = (
    <HanStreakFlame
      accessible={false}
      size={size}
      testID={
        isTodaySecured
          ? "learn-review-recharge-streak-kept"
          : "learn-review-recharge-streak-at-stake"
      }
      variant={isTodaySecured ? "active" : "inactive"}
    />
  );
  if (isInactive) {
    return <View className="h-full items-center justify-center">{flame}</View>;
  }
  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger
        accessibilityLabel={
          isTodaySecured ? "Streak kept today" : `Streak at stake: ${pointsLeft} more points today`
        }
        accessibilityRole="button"
        className="h-full items-center justify-center"
        hitSlop={{ bottom: 8, top: 8 }}
        testID="learn-review-recharge-streak"
      >
        {flame}
      </TooltipTrigger>
      <TooltipContent className="max-w-72" side="bottom">
        <Text className="font-semibold">Daily Streak</Text>
        <Text>
          {isTodaySecured
            ? "Today counts toward your streak."
            : `${pointsLeft} more points today keeps your streak. Again earns 1; Hard, Good and Easy earn 5.`}
        </Text>
      </TooltipContent>
    </Tooltip>
  );
}
